
import { ChangeEvent, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Swal from 'sweetalert2';
import { showErrorAlert } from '../../helpers/alertHelper';
import { getAuthHeader } from '../../helpers/authHelper';
import DonationModel from '../../models/DonationModel';
import Loading from '../shared/Loading';

// Define a volunteer interface
interface Volunteer {
    id: number;
    name: string;
}

interface GetDonationsResponse {
    donations: DonationModel[];
}

interface GetVolunteerListResponse {
    volunteers: Volunteer[];
}

const fetchDonations = async (): Promise<GetDonationsResponse> => {
    const resp = await fetch("/api/admin/get-donations", {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': getAuthHeader(),
        }
    });
    if (!resp.ok) throw new Error("Failed to fetch donations");
    return resp.json();
};

const fetchVolunteerList = async (): Promise<GetVolunteerListResponse> => {
    const resp = await fetch("/api/admin/get-volunteer-list", {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': getAuthHeader(),
        }
    });
    if (!resp.ok) throw new Error("Failed to fetch volunteers");
    return resp.json();
};

const Donations = () => {
    const queryClient = useQueryClient();

    //
    // State
    //
    const [searchTerm, setSearchTerm] = useState('');
    const [notifying, setNotifying] = useState<number | null>(null);
    const [notifyingAll, setNotifyingAll] = useState<boolean>(false);
    const [newDonation, setNewDonation] = useState({
        type: 'Basket',
        name: '',
        collectedBy: '',
        value: 50,
        winnerName: '',
        winnerPhoneNumber: '',
        phoneError: false
    });
    const [editIndex, setEditIndex] = useState<number | null>(null);
    const [editedDonation, setEditedDonation] = useState({
        donationId: 0,
        type: 'Basket',
        name: '',
        collectedBy: '',
        value: 50,
        winnerName: '',
        winnerPhoneNumber: '',
        phoneError: false
    });

    //
    // Queries
    //
    const { data: donationsData, isLoading: loading } = useQuery<GetDonationsResponse>({
        queryKey: ['donations'],
        queryFn: fetchDonations,
    });

    const { data: volunteersData, isLoading: volunteersLoading } = useQuery<GetVolunteerListResponse>({
        queryKey: ['volunteerList'],
        queryFn: fetchVolunteerList,
    });

    const donations = donationsData?.donations ?? [];
    const volunteers = volunteersData?.volunteers ?? [];
    const visibleDonations = searchTerm
        ? donations.filter(d => d.name?.toLowerCase().includes(searchTerm))
        : donations;

    //
    // Mutations
    //
    const addMutation = useMutation({
        mutationFn: async (donation: { type: string; name: string; collectedBy: string; value: number; winnerName: string; winnerPhoneNumber: string }) => {
            const resp = await fetch("/api/admin/add-donation", {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': getAuthHeader(),
                },
                body: JSON.stringify(donation)
            });
            if (!resp.ok) throw new Error("Failed to add donation");
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['donations'] });
            setNewDonation({
                type: 'Basket',
                name: '',
                collectedBy: '',
                value: 50,
                winnerName: '',
                winnerPhoneNumber: '',
                phoneError: false
            });
        },
        onError: () => showErrorAlert(),
    });

    const editMutation = useMutation({
        mutationFn: async (donation: { donationId: number; type: string; name: string; collectedBy: string; value: number; winnerName: string; winnerPhoneNumber: string }) => {
            const resp = await fetch("/api/admin/edit-donation", {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': getAuthHeader(),
                },
                body: JSON.stringify(donation)
            });
            if (!resp.ok) throw new Error("Failed to edit donation");
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['donations'] });
            setEditIndex(null);
        },
        onError: () => showErrorAlert(),
    });

    const deleteMutation = useMutation({
        mutationFn: async (donationId: number) => {
            const resp = await fetch(`/api/admin/delete-donation/${donationId}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': getAuthHeader(),
                }
            });
            if (!resp.ok) throw new Error("Failed to delete donation");
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['donations'] });
        },
        onError: () => showErrorAlert(),
    });

    //
    // Handle search
    //
    function handleSearch(e: ChangeEvent<HTMLInputElement>): void {
        setSearchTerm(e.target.value.toLowerCase());
    }

    // Phone number validation and formatting
    const formatPhoneNumber = (phoneNumber: string | undefined | null): string => {
        // Handle null/undefined
        if (phoneNumber === null || phoneNumber === undefined) return '';

        // Strip all non-numeric characters
        const cleaned = phoneNumber.replace(/\D/g, '');

        // Format as (XXX) XXX-XXXX if we have enough digits
        if (cleaned.length >= 10) {
            return `(${cleaned.substring(0, 3)}) ${cleaned.substring(3, 6)}-${cleaned.substring(6, 10)}`;
        }

        // Otherwise return the cleaned number
        return cleaned;
    };

    const validatePhoneNumber = (phoneNumber: string | undefined | null): boolean => {
        // If empty, null or undefined, consider it valid (since phone might be optional)
        if (!phoneNumber) return true;

        // Check if we have exactly 10 digits after stripping non-numeric characters
        const cleaned = phoneNumber.replace(/\D/g, '');
        return cleaned.length === 10;
    };

    // Normalize phone number for API (strip formatting)
    const normalizePhoneNumber = (phoneNumber: string | undefined | null): string => {
        // Handle null/undefined
        if (phoneNumber === null || phoneNumber === undefined) return '';

        // Remove non-numeric characters
        let normalized = phoneNumber.replace(/\D/g, '');

        // If it starts with 1 and is 11 digits, remove the 1
        if (normalized.length === 11 && normalized.startsWith('1')) {
            normalized = normalized.substring(1);
        }

        return normalized;
    };

    // Format phone for display in the table
    const formatPhoneForDisplay = (phoneNumber: string | undefined | null): string => {
        // Handle null/undefined
        if (phoneNumber === null || phoneNumber === undefined) return '';

        // If it's already formatted, return as is
        if (phoneNumber.includes('(') && phoneNumber.includes(')')) {
            return phoneNumber;
        }

        // Otherwise format it
        return formatPhoneNumber(phoneNumber);
    };

    // Check if a donation has a valid phone number
    const hasValidPhoneNumber = (donation: DonationModel): boolean => {
        return validatePhoneNumber(donation.winnerPhoneNumber) &&
            donation.winnerPhoneNumber !== null &&
            donation.winnerPhoneNumber !== undefined &&
            donation.winnerPhoneNumber !== '';
    };

    // Handle adding new donation
    const handleAddDonation = () => {
        if (!newDonation.type || !newDonation.name || !newDonation.collectedBy || !newDonation.value) {
            return;
        }

        // Validate phone number
        if (!validatePhoneNumber(newDonation.winnerPhoneNumber)) {
            setNewDonation({ ...newDonation, phoneError: true });
            return;
        }

        // Remove the phoneError field before sending to API
        const { phoneError, ...donationToAdd } = {
            ...newDonation,
            winnerPhoneNumber: normalizePhoneNumber(newDonation.winnerPhoneNumber)
        };

        addMutation.mutate(donationToAdd);
    };

    // Handle editing donation
    const handleEditDonation = (donation: DonationModel) => {
        setEditIndex(visibleDonations.indexOf(donation));

        // Format the phone number for editing
        const formattedPhone = formatPhoneNumber(donation.winnerPhoneNumber);

        setEditedDonation({
            donationId: donation.donationId,
            type: donation.type || '',
            name: donation.name || '',
            collectedBy: donation.collectedBy || '',
            value: donation.value || 50,
            winnerName: donation.winnerName || '',
            winnerPhoneNumber: formattedPhone,
            phoneError: false
        });
    };

    // Handle save edit
    const handleSaveEdit = () => {
        if (editIndex === null) {
            return;
        }

        // Validate phone number
        if (!validatePhoneNumber(editedDonation.winnerPhoneNumber)) {
            setEditedDonation({ ...editedDonation, phoneError: true });
            return;
        }

        // Remove the phoneError field before sending to API
        const { phoneError, ...donationToUpdate } = {
            ...editedDonation,
            winnerPhoneNumber: normalizePhoneNumber(editedDonation.winnerPhoneNumber)
        };

        editMutation.mutate(donationToUpdate);
    };

    // Handle delete donation
    const handleDeleteDonation = (index: number) => {
        const donationId = visibleDonations[index].donationId;

        Swal.fire({
            title: 'Are you sure?',
            text: "This will permanently delete the Donation.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Delete'
        }).then((result) => {
            if (result.isConfirmed) {
                deleteMutation.mutate(donationId);
            }
        });
    };

    // Handle notifying a single winner
    const handleNotifyWinner = (donationId: number) => {
        setNotifying(donationId);

        fetch(`/api/admin/notify-winner/${donationId}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': getAuthHeader(),
            }
        }).then((response) => {
            if (response.ok) {
                return response.json();
            } else {
                throw new Error('Failed to notify winner');
            }
        }).then(data => {
            if (data.success) {
                Swal.fire({
                    title: 'Success!',
                    text: 'Winner has been notified successfully.',
                    icon: 'success',
                    confirmButtonColor: '#3085d6'
                });
            } else {
                Swal.fire({
                    title: 'Warning',
                    text: data.message || 'Could not notify winner.',
                    icon: 'warning',
                    confirmButtonColor: '#3085d6'
                });
            }
        }).catch(error => {
            Swal.fire({
                title: 'Error!',
                text: 'Failed to notify the winner. Please try again.',
                icon: 'error',
                confirmButtonColor: '#3085d6'
            });
            console.error("Error notifying winner:", error);
        }).finally(() => {
            setNotifying(null);
        });
    };

    // Handle notifying all winners
    const handleNotifyAllWinners = () => {
        setNotifyingAll(true);

        Swal.fire({
            title: 'Notify all winners?',
            text: "This will send a notification to all winners with valid phone numbers.",
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Notify All'
        }).then((result) => {
            if (result.isConfirmed) {
                try {
                    fetch('/api/admin/notify-winners', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': getAuthHeader(),
                        }
                    }).then((response) => {
                        if (response.ok) {
                            return response.json();
                        } else {
                            throw new Error('Failed to notify winners');
                        }
                    }).then(data => {
                        if (data.success) {
                            Swal.fire({
                                title: 'Success!',
                                text: 'All winners have been notified successfully.',
                                icon: 'success',
                                confirmButtonColor: '#3085d6'
                            });
                        } else {
                            Swal.fire({
                                title: 'Warning',
                                text: data.message || 'Could not notify all winners.',
                                icon: 'warning',
                                confirmButtonColor: '#3085d6'
                            });
                        }
                    }).catch(error => {
                        Swal.fire({
                            title: 'Error!',
                            text: 'Failed to notify winners. Please try again.',
                            icon: 'error',
                            confirmButtonColor: '#3085d6'
                        });
                        console.error("Error notifying winners:", error);
                    }).finally(() => {
                        setNotifyingAll(false);
                    });
                } catch (error) {
                    console.error("Error notifying winners:", error);
                    showErrorAlert();
                    setNotifyingAll(false);
                }
            } else {
                setNotifyingAll(false);
            }
        });
    };

    // Handle input change for new donation and edited donation
    const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>, isEdit: boolean = false) => {
        const { name, value } = e.target;

        // Special handling for phone numbers
        if (name === 'winnerPhoneNumber') {
            // Format the phone number
            const formattedPhone = formatPhoneNumber(value);
            const isValid = validatePhoneNumber(formattedPhone);

            if (isEdit) {
                setEditedDonation({
                    ...editedDonation,
                    [name]: formattedPhone,
                    // Only show an error if there's a value and it's invalid
                    phoneError: !isValid && formattedPhone.length > 0
                });
            } else {
                setNewDonation({
                    ...newDonation,
                    [name]: formattedPhone,
                    // Only show an error if there's a value and it's invalid
                    phoneError: !isValid && formattedPhone.length > 0
                });
            }
        } else {
            // Regular handling for other fields
            if (isEdit) {
                setEditedDonation({ ...editedDonation, [name]: value });
            } else {
                setNewDonation({ ...newDonation, [name]: value });
            }
        }
    };

    // Render volunteer options for dropdowns
    const renderVolunteerOptions = () => {
        if (volunteersLoading) {
            return <option value="">Loading volunteers...</option>;
        }

        if (!volunteers || volunteers.length === 0) {
            return <option value="">No volunteers available</option>;
        }

        return (
            <>
                <option value="">Select Volunteer</option>
                {volunteers.map(volunteer => (
                    <option key={volunteer.id} value={volunteer.name}>
                        {volunteer.name}
                    </option>
                ))}
            </>
        );
    };

    // Check if there are any donations with valid phone numbers to notify
    const hasWinnersToNotify = visibleDonations && visibleDonations.some(hasValidPhoneNumber);

    return (
        <div className="container p-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h2 className="mb-0">Donations</h2>
                <button
                    className="btn btn-info"
                    onClick={handleNotifyAllWinners}
                    disabled={!hasWinnersToNotify || notifyingAll || loading}
                >
                    {notifyingAll ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Notifying...
                        </>
                    ) : (
                        <>
                            <i className="bi bi-bell me-2"></i>
                            Notify All Winners
                        </>
                    )}
                </button>
            </div>

            {loading && <Loading />}

            <div className="my-3 row justify-content-center">
                <div className="col-12 col-md-8">
                    <div className="input-group">
                        <div className="input-group-prepend">
                            <div className="input-group-text search-input-icon py-2">
                                <i className="bi bi-search"></i>
                            </div>
                        </div>
                        <input
                            className="form-control py-2 border-right-0 border"
                            type="search"
                            placeholder="Search"
                            onChange={(e) => { handleSearch(e) }}
                        />
                    </div>
                </div>
            </div>

            <div className="table-responsive">
                <table className="table table-striped table-hover table-bordered">
                    <thead>
                        <tr>
                            <th>Type</th>
                            <th>Name</th>
                            <th>Volunteer Name</th>
                            <th>Value</th>
                            <th>Winner Name</th>
                            <th>Winner Phone Number</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {visibleDonations && visibleDonations.map((donation, index) => (
                            <tr key={index}>
                                {editIndex === index ? (
                                    <>
                                        <td>
                                            <select
                                                className="form-control"
                                                name="type"
                                                value={editedDonation.type}
                                                onChange={(e) => handleInputChange(e, true)}
                                            >
                                                <option value="Basket">Basket</option>
                                            </select>
                                        </td>
                                        <td>
                                            <input
                                                className="form-control"
                                                type="text"
                                                name="name"
                                                value={editedDonation.name}
                                                onChange={(e) => handleInputChange(e, true)}
                                            />
                                        </td>
                                        <td>
                                            <select
                                                className="form-control"
                                                name="collectedBy"
                                                value={editedDonation.collectedBy}
                                                onChange={(e) => handleInputChange(e, true)}
                                            >
                                                {renderVolunteerOptions()}
                                            </select>
                                        </td>
                                        <td>
                                            <select
                                                className="form-control"
                                                name="value"
                                                value={editedDonation.value}
                                                onChange={(e) => handleInputChange(e, true)}
                                            >
                                                <option value={50}>$50</option>
                                                <option value={200}>$200</option>
                                            </select>
                                        </td>
                                        <td>
                                            <input
                                                className="form-control"
                                                type="text"
                                                name="winnerName"
                                                value={editedDonation.winnerName}
                                                onChange={(e) => handleInputChange(e, true)}
                                            />
                                        </td>
                                        <td>
                                            <div className="form-group">
                                                <input
                                                    className={`form-control ${editedDonation.phoneError ? 'is-invalid' : ''}`}
                                                    type="text"
                                                    name="winnerPhoneNumber"
                                                    value={editedDonation.winnerPhoneNumber}
                                                    onChange={(e) => handleInputChange(e, true)}
                                                    placeholder="(123) 456-7890"
                                                />
                                                {editedDonation.phoneError && (
                                                    <div className="invalid-feedback">
                                                        Please enter a valid 10-digit phone number
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <button
                                                className="btn btn-primary m-1"
                                                onClick={() => handleSaveEdit()}
                                                disabled={editedDonation.phoneError}
                                            >Save</button>
                                            <button className="btn btn-danger" onClick={() => setEditIndex(null)}>Cancel</button>
                                        </td>
                                    </>
                                ) : (
                                    <>
                                        <td>{donation.type}</td>
                                        <td>{donation.name}</td>
                                        <td>{donation.collectedBy}</td>
                                        <td>${donation.value}</td>
                                        <td>{donation.winnerName}</td>
                                        <td>{formatPhoneForDisplay(donation.winnerPhoneNumber)}</td>
                                        <td>
                                            <div className="d-flex flex-wrap">
                                                <button className="btn btn-primary m-1" onClick={() => handleEditDonation(donation)}>
                                                    <i className="bi bi-pencil me-1"></i>Edit
                                                </button>
                                                <button className="btn btn-danger m-1" onClick={() => handleDeleteDonation(index)}>
                                                    <i className="bi bi-trash me-1"></i>Delete
                                                </button>
                                                {hasValidPhoneNumber(donation) && (
                                                    <button
                                                        className="btn btn-info m-1"
                                                        onClick={() => handleNotifyWinner(donation.donationId)}
                                                        disabled={notifying === donation.donationId}
                                                    >
                                                        {notifying === donation.donationId ? (
                                                            <>
                                                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                                                Sending...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className="bi bi-bell me-1"></i>Notify
                                                            </>
                                                        )}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </>
                                )}
                            </tr>
                        ))}
                        <tr>
                            <td>
                                <select
                                    className="form-control"
                                    name="type"
                                    value={newDonation.type}
                                    onChange={(e) => handleInputChange(e, false)}
                                >
                                    <option value="Basket">Basket</option>
                                </select>
                            </td>
                            <td>
                                <input
                                    className="form-control"
                                    type="text"
                                    name="name"
                                    value={newDonation.name}
                                    onChange={(e) => handleInputChange(e, false)}
                                />
                            </td>
                            <td>
                                <select
                                    className="form-control"
                                    name="collectedBy"
                                    value={newDonation.collectedBy}
                                    onChange={(e) => handleInputChange(e, false)}
                                >
                                    {renderVolunteerOptions()}
                                </select>
                            </td>
                            <td>
                                <select
                                    className="form-control"
                                    name="value"
                                    value={newDonation.value}
                                    onChange={(e) => handleInputChange(e, false)}
                                >
                                    <option value={50}>$50</option>
                                    <option value={200}>$200</option>
                                </select>
                            </td>
                            <td>
                                <input
                                    className="form-control"
                                    type="text"
                                    name="winnerName"
                                    value={newDonation.winnerName}
                                    onChange={(e) => handleInputChange(e, false)}
                                />
                            </td>
                            <td>
                                <div className="form-group">
                                    <input
                                        className={`form-control ${newDonation.phoneError ? 'is-invalid' : ''}`}
                                        type="text"
                                        name="winnerPhoneNumber"
                                        value={newDonation.winnerPhoneNumber}
                                        onChange={(e) => handleInputChange(e, false)}
                                        placeholder="(123) 456-7890"
                                    />
                                    {newDonation.phoneError && (
                                        <div className="invalid-feedback">
                                            Please enter a valid 10-digit phone number
                                        </div>
                                    )}
                                </div>
                            </td>
                            <td>
                                <button
                                    className="btn btn-success m-1"
                                    onClick={handleAddDonation}
                                    disabled={!newDonation.type || !newDonation.name || !newDonation.collectedBy || !newDonation.value || newDonation.phoneError}
                                >
                                    <i className="bi bi-plus-circle me-1"></i>Add
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default Donations;